import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GetVlpUsers, DeleteVlpUser } from "../../../services/UsersService";
import CommonLoader from "../../common/CommonLoader";
import ViewUser from "./ViewUser";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";

const UsersTable = () => {
  const columns = [
    "Operator",
    "AliasName",
    "FullName",
    "Email",
    "Title",
    "Actions",
  ];
  const columnHeaders = ["operator", "aliasName", "fullName", "email", "title"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIdToDelete, setSelectedUserIdToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await GetVlpUsers();
      setUsersData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filteredResults = usersData.filter((user) =>
      columnHeaders.some((header) =>
        user[header].toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, usersData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (user) => {
    setSelectedUser(user);
  };
  const handleEditClick = (operator) => {
    navigate("/vlp/edit-user/" + operator);
  };
  const handleManageGroupsClick = (operator) => {
    navigate("/vlp/edit-user-groups/" + operator);
  };
  const handleBackClick = () => {
    setSelectedUser(null);
    setSearchTerm("");
  };

  const handleAddUserClick = () => {
    navigate("/vlp/add-user");
    // Handle the action when the "+ New User" button is clicked
    // You can navigate to the add user page or open a modal, etc.
  };

  const handleDeleteClick = (userId) => {
    setSelectedUserIdToDelete(userId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteVlpUser(selectedUserIdToDelete);
      if (response.results === true) {
        NotificationManager.success("User deleted successfully");
        setIsDeleteConfirmationOpen(false);
        await fetchUsers();
      } else {
        NotificationManager.error("Error deleting user");
      }
    } catch (error) {
      NotificationManager.error("Error deleting user");
    }
  };

  const renderActions = (user) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(user.operator)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(user)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="manage-groups"
        style={{ color: "#007DD1" }}
        onClick={() => handleManageGroupsClick(user.operator)}
        title="Manage Access Groups"
      >
        <GroupIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(user.operator)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Users </span>
      <hr />
      {selectedUser ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewUser user={selectedUser} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddUserClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer
              sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}
            >
              <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user, index) => (
                      <TableRow
                        key={user.operator}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{user[column]}</TableCell>
                        ))}
                        <TableCell>{renderActions(user)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this user?"
        color="red"
      />
    </>
  );
};

export default UsersTable;
